/**
 * Utilitários de cálculo e formatação para o DRE.
 * Totalmente adaptado para a Arquitetura PURA de Contabilidade (Regime de Competência).
 */
import { FINANCIAL_TYPES } from "../../../../constants/financial";

/**
 * Formata valor em BRL (Real brasileiro).
 */
export const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

/**
 * Formata variação percentual.
 */
export const formatPercent = (value) => {
    const num = Number(value) || 0;
    return `${num >= 0 ? "+" : ""}${num.toFixed(1)}%`;
};

/**
 * Calcula variação percentual entre dois valores.
 */
export const calcVariation = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
};

/**
 * Agrupa accountingRecords por categoria para montar o DRE absoluto.
 *
 * @param {Array} records - Registros contábeis do período (Somente Competência)
 * @param {Array} categories - Categorias financeiras (plano de contas)
 * @returns {Object} { income: [...], expense: [...], totals: {...} }
 */
export const buildDREData = (records, categories) => {
    // Agrupar categorias por tipo (AUI SOMENTE RECEITA E DESPESA contam no DRE final)
    const incomeCategories = categories.filter(c => c.type === FINANCIAL_TYPES.INCOME).sort((a, b) => (a.order || 0) - (b.order || 0));
    const expenseCategories = categories.filter(c => c.type === FINANCIAL_TYPES.EXPENSE).sort((a, b) => (a.order || 0) - (b.order || 0));

    // Calcular totais absolutos por categoria
    const categoryTotals = {};

    for (const record of records) {
        let catId = record.idCategory;
        if (!catId) {
            catId = record.type === FINANCIAL_TYPES.INCOME ? "uncategorized_INCOME" : "uncategorized_EXPENSE";
        }

        if (!categoryTotals[catId]) {
            categoryTotals[catId] = {
                amount: 0,
                count: 0,
            };
        }
        // O accountingRecord usa 'amount' garantidamente positivo e puro.
        categoryTotals[catId].amount += Number(record.amount) || 0;
        categoryTotals[catId].count += 1;
    }

    // Montar linhas do DRE para RECEITAS
    const incomeLines = incomeCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        order: cat.order,
        isSystem: cat.isSystem,
        amount: categoryTotals[cat.id]?.amount || 0,
        count: categoryTotals[cat.id]?.count || 0,
    })).filter(line => line.amount > 0 || line.isSystem); // Mostra as do sistema mesmo zeradas

    if (categoryTotals["uncategorized_INCOME"]?.amount > 0) {
        incomeLines.push({
            id: "uncategorized_INCOME",
            name: "Outras Receitas (Sem Categoria)",
            order: 999,
            isSystem: false,
            amount: categoryTotals["uncategorized_INCOME"].amount,
            count: categoryTotals["uncategorized_INCOME"].count,
        });
    }

    // Montar linhas do DRE para DESPESAS/CUSTOS
    const expenseLines = expenseCategories.map(cat => ({
        id: cat.id,
        name: cat.name,
        order: cat.order,
        isSystem: cat.isSystem,
        amount: categoryTotals[cat.id]?.amount || 0,
        count: categoryTotals[cat.id]?.count || 0,
    })).filter(line => line.amount > 0 || line.isSystem); // O banco já converte custos como expense pura.

    if (categoryTotals["uncategorized_EXPENSE"]?.amount > 0) {
        expenseLines.push({
            id: "uncategorized_EXPENSE",
            name: "Outras Despesas (Sem Categoria)",
            order: 999,
            isSystem: false,
            amount: categoryTotals["uncategorized_EXPENSE"].amount,
            count: categoryTotals["uncategorized_EXPENSE"].count,
        });
    }

    // Totais gerais contábeis
    const totalIncome = incomeLines.reduce((sum, l) => sum + l.amount, 0);
    const totalExpense = expenseLines.reduce((sum, l) => sum + l.amount, 0);
    const netResult = totalIncome - totalExpense;

    return {
        income: incomeLines,
        expense: expenseLines,
        totals: {
            totalIncome,
            totalExpense,
            netResult,
            margin: totalIncome > 0 ? (netResult / totalIncome * 100) : 0,
        }
    };
};

/**
 * Filtra registros por período mensal (Data de Competência).
 * @param {Array} records - Registros
 * @param {number} year - Ano
 * @param {number} month - Mês (1-12)
 * @returns {Array} Registros filtrados
 */
export const filterByMonth = (records, year, month) => {
    const prefix = `${year}-${String(month).padStart(2, "0")}`;
    return records.filter(r => {
        const date = r.competenceDate || "";
        return date.startsWith(prefix);
    });
};

/**
 * Filtra registros por trimestre (Data de Competência).
 */
export const filterByQuarter = (records, year, quarter) => {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    return records.filter(r => {
        const date = r.competenceDate || "";
        if (!date.startsWith(`${year}-`)) return false;
        const month = parseInt(date.split("-")[1]);
        return month >= startMonth && month <= endMonth;
    });
};
